import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield, Users, Truck, Heart } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';

const UeberUns = () => {
  return (
    <div className="bg-white py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Breadcrumb items={[{ label: 'Über uns' }]} />
        
        <h1 className="text-4xl font-bold text-[#0F172A] mb-6">Über Schössow Dienstleistungen</h1>
        <p className="text-lg text-gray-700 mb-12 max-w-3xl">
          Seit Jahren sind wir Ihr zuverlässiger Partner für Haushalts- und Wohnungsauflösungen in Dortmund. 
          Schnell, diskret, zum Festpreis – das ist unser Versprechen.
        </p>

        {/* Story */}
        <section className="mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-6">Unsere Geschichte</h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Schössow Dienstleistungen wurde gegründet, um Menschen in Dortmund bei einer oft belastenden 
                Aufgabe zu unterstützen: der Auflösung von Haushalten. Ob nach einem Todesfall, bei Umzug 
                oder in schwierigen Lebenssituationen – wir wissen, wie emotional und zeitintensiv diese 
                Aufgabe sein kann.
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Unser Anspruch ist es, diese Herausforderung für Sie so einfach und fair wie möglich zu gestalten. 
                Mit transparenten Festpreisen, professioneller Arbeit und respektvollem Umgang haben wir uns 
                das Vertrauen zahlreicher Kunden in Dortmund erarbeitet.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Als lokaler Anbieter kennen wir die Besonderheiten der Stadt – von engen Altbau-Treppenhäusern 
                bis zu Parkzonen in der Innenstadt. Diese Expertise und unsere kurzen Anfahrtswege ermöglichen 
                uns schnelle, faire und zuverlässige Lösungen.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0ZWFtfGVufDB8fHx8MTc2MjQ1ODY5M3ww&ixlib=rb-4.1.0&q=85"
                alt="Schössow Dienstleistungen Team - Zusammenarbeit"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-8 text-center">Unsere Werte</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <Shield className="h-12 w-12 text-[#16A34A] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Zuverlässigkeit</h3>
              <p className="text-gray-700">
                Pünktlich, professionell und verbindlich. Auf uns können Sie sich verlassen.
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <Heart className="h-12 w-12 text-[#16A34A] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Diskretion</h3>
              <p className="text-gray-700">
                Respektvoller Umgang mit Ihren Räumlichkeiten und persönlichen Gegenständen.
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <Users className="h-12 w-12 text-[#16A34A] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Transparenz</h3>
              <p className="text-gray-700">
                Klare Festpreise, faire Wertanrechnung, keine versteckten Kosten.
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <Truck className="h-12 w-12 text-[#16A34A] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Effizienz</h3>
              <p className="text-gray-700">
                Schnelle Termine, zügige Durchführung, besenreine Übergabe.
              </p>
            </div>
          </div>
        </section>

        {/* Qualifications */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-6">Qualifikation & Versicherung</h2>
          <div className="bg-gray-50 rounded-lg p-8">
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="text-[#16A34A] font-bold text-xl flex-shrink-0">✓</span>
                <span className="text-gray-700">
                  <strong>Versichertes Team:</strong> Alle Mitarbeiter sind versichert. Schäden sind abgedeckt.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#16A34A] font-bold text-xl flex-shrink-0">✓</span>
                <span className="text-gray-700">
                  <strong>Erfahrung:</strong> Mehrjährige Expertise in Haushaltsauflösungen und Entrümpelung.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#16A34A] font-bold text-xl flex-shrink-0">✓</span>
                <span className="text-gray-700">
                  <strong>Entsorgungspartner:</strong> Zusammenarbeit mit zertifizierten Entsorgungsbetrieben.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#16A34A] font-bold text-xl flex-shrink-0">✓</span>
                <span className="text-gray-700">
                  <strong>Gesetzeskonform:</strong> Alle Entsorgungen erfolgen nach Vorschrift mit Dokumentation.
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* Team Image */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-6">Unser Team</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <img
              src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjB0ZWFtfGVufDB8fHx8MTc2MjQ1ODY5M3ww&ixlib=rb-4.1.0&q=85"
              alt="Professionelles Team bei der Besprechung"
              className="rounded-lg shadow-md w-full h-80 object-cover"
            />
            <img
              src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjB0ZWFtfGVufDB8fHx8MTc2MjQ1ODY5M3ww&ixlib=rb-4.1.0&q=85"
              alt="Team-Meeting zur Planung"
              className="rounded-lg shadow-md w-full h-80 object-cover"
            />
          </div>
          <p className="text-gray-700 mt-6">
            Unser Team besteht aus erfahrenen Mitarbeitern, die professionell, diskret und respektvoll arbeiten. 
            Jeder kennt die Besonderheiten von Haushaltsauflösungen und geht sensibel mit Ihrer Situation um.
          </p>
        </section>

        {/* CTA */}
        <div className="bg-[#0F172A] text-white rounded-lg p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Lernen Sie uns kennen</h2>
          <p className="text-lg mb-8 text-gray-300 max-w-2xl mx-auto">
            Rufen Sie uns an oder fordern Sie ein Angebot an. Wir freuen uns auf Ihre Anfrage.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#16A34A] hover:bg-[#16A34A]/90 text-white">
              <Link to="/kontakt">Jetzt anfragen</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white text-[#0F172A] hover:bg-gray-100">
              <a href="tel:+4923139085116">0231 39085116</a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UeberUns;
